<template>
  <div
    class="patient-system-params"
    v-loading="loading"
    element-loading-text="正在进行患者系统参数的变更..."
    element-loading-background="rgba(255, 255, 255, 0.75)"
    element-loading-custom-class="patient-params-loading"
  >
    <!-- ============ 页面标题 ============ -->
    <div class="page-header">
      <div class="header-left">
        <h1 class="page-title">患者管理参数配置</h1>
        <p class="page-subtitle">渐进升级模型 · 四类互斥不重复 · 业务完成时实时判定（不回潮重算）</p>
      </div>
      <div class="header-right">
        <el-button
          class="reset-btn"
          size="small"
          icon="el-icon-refresh"
          @click="handleReset"
        >恢复默认</el-button>
        <el-button
          class="save-btn"
          size="small"
          icon="el-icon-xmdz"
          :disabled="!hasChanges"
          @click="handleSave"
        >保存配置</el-button>
      </div>
    </div>

    <!-- ============ 规则实时预览 ============ -->
    <el-card class="section-card preview-card" shadow="hover">
      <div slot="header" class="card-header">
        <div class="header-left">
          <i class="el-icon-s-operation card-icon"><span class="card-title">规则实时预览</span></i>
          <span class="card-tip">
            基于当前全部患者样本，按当前规则实时计算分类结果，调整参数即时更新。
            <span v-if="activeIndex !== null" class="reset-tip" @click="resetCardSelection">
              （点击已选中的卡片可取消筛选）
            </span>
          </span>
        </div>
      </div>

      <div class="preview-body">
        <div class="donut-wrapper">
          <div ref="donutChart" class="donut-chart"></div>
          <div class="donut-center">
            <div class="donut-center-num" :class="{ 'is-all': activeIndex === null }">
              {{ activePhrLabel }}
            </div>
            <div class="donut-center-label">{{ currentCenterCount }} 人</div>
          </div>
        </div>

        <!-- 走马灯卡片容器（纯 CSS 过渡） -->
        <div class="stat-cards" :class="{ 'has-active': activeIndex !== null }">
          <div
            v-for="(item, index) in cardList"
            :key="item.key"
            class="stat-card-item"
            :class="{
              active: activeIndex === index,
              dimmed: activeIndex !== null && activeIndex !== index
            }"
            @click="handleCardClick(index)"
            @mouseenter="hoverIndex = index"
            @mouseleave="hoverIndex = null"
          >
            <div class="stat-card-header">
              <span class="stat-dot" :style="{ background: item.color }"></span>
              <span class="stat-card-label">{{ item.label }}</span>
            </div>
            <div class="stat-card-value">
              {{ item.count }}<span class="unit">人</span>
            </div>
            <div v-if="item.extra" class="stat-card-extra">{{ item.extra }}</div>
            <div v-if="activeIndex === index" class="stat-card-close-tip">
              <i class="el-icon-close"></i>
            </div>
          </div>
        </div>
      </div>
    </el-card>

    <!-- ============ 患者分类递进参数 ============ -->
    <el-card class="section-card params-card" shadow="hover">
      <div slot="header" class="card-header">
        <div class="header-left">
          <i class="el-icon-s-help card-icon"><span class="card-title">患者分类递进参数</span></i>
          <span class="card-tip">四类递进升级、互斥不重复，业务完成时实时判定，不可手动修改分类；重点关注患者支持手动标记。</span>
        </div>
      </div>

      <!-- 流转说明 -->
      <div class="flow-tip">
        <div class="flow-row">
          <span class="flow-tag flow-normal">普通患者</span>
          <i class="el-icon-arrow-left flow-arrow"></i>
          <span class="flow-text">近 {{ visitStatPeriod || 'N' }} 月内就诊达标</span>
          <span class="flow-tag flow-core">核心患者</span>
          <i class="el-icon-arrow-left flow-arrow"></i>
          <span class="flow-text">同种治疗/药品规律开具 或 连续用药/手动标记</span>
          <span class="flow-tag flow-focus">重点患者</span>
        </div>
        <div class="flow-row">
          <span class="flow-tag flow-core">核心/重点患者</span>
          <i class="el-icon-arrow-left flow-arrow"></i>
          <span class="flow-text">最后就诊超期</span>
          <span class="flow-tag flow-lost">流失核心患者</span>
          <i class="el-icon-arrow-left flow-arrow"></i>
          <span class="flow-text">重新就诊，恢复对应层级</span>
        </div>
        <div class="flow-row flow-row-note">
          <span class="flow-note">业务完成时实时判定，不做每日凌晨全量重算。</span>
        </div>
      </div>

      <!-- ===== 第 1 层：普通患者 → 核心患者 ===== -->
      <div class="layer-block">
        <div class="layer-header">
          <span class="layer-badge">1</span>
          <span class="layer-title">第1层：普通患者 → 核心患者</span>
        </div>
        <!-- ★ 动态绑定 visitStatPeriod -->
        <div class="layer-desc">
          只看「近 {{ visitStatPeriod || 'N' }} 个月内」的就诊记录（历史累计不再计入）：窗口内累计就诊次数达到阈值，即从普通患者自动升级为核心患者。
        </div>
        <div class="param-grid">
          <div v-if="hasField('visitStatPeriod')" class="param-item">
            <span class="param-label">
              <span
                class="status-dot"
                :class="isFieldDisabled('visitStatPeriod') ? 'off' : 'on'"
                :title="iconTooltip('visitStatPeriod')"
                @click="handleToggleFromDot('visitStatPeriod')"
              ></span>
              就诊统计周期
            </span>
            <div class="param-input-wrap">
              <el-input-number
                v-model="visitStatPeriod"
                :min="1"
                :step="1"
                size="small"
                controls-position="right"
                :disabled="isFieldDisabled('visitStatPeriod')"
                @change="handleFieldChange('visitStatPeriod')"
              ></el-input-number>
              <span class="param-unit">个月</span>
            </div>
            <!-- ★ 动态绑定 visitStatPeriod -->
            <div class="hint-block">第1层判定窗口：只统计「近 {{ visitStatPeriod || 'N' }} 个月内」的就诊次数，历史累计不再计入</div>
          </div>

          <div v-if="hasField('coreMinVisits')" class="param-item">
            <span class="param-label">
              <span
                class="status-dot"
                :class="isFieldDisabled('coreMinVisits') ? 'off' : 'on'"
                :title="iconTooltip('coreMinVisits')"
                @click="handleToggleFromDot('coreMinVisits')"
              ></span>
              累计就诊次数阈值
            </span>
            <div class="param-input-wrap">
              <el-input-number
                v-model="coreMinVisits"
                :min="1"
                :step="1"
                size="small"
                controls-position="right"
                :disabled="isFieldDisabled('coreMinVisits')"
                @change="handleFieldChange('coreMinVisits')"
              ></el-input-number>
              <span class="param-unit">次</span>
            </div>
            <div class="hint-block">近统计周期内累计就诊达到该次数即升级为「核心患者」（递进第1层）</div>
          </div>
        </div>
        <!-- ★ 动态绑定 visitStatPeriod + coreMinVisits -->
        <div class="layer-example">
          例：统计周期 {{ visitStatPeriod || 'N' }} 个月 + 阈值 {{ coreMinVisits || 'M' }} 次 → 近 {{ visitStatPeriod || 'N' }} 个月内累计就诊 ≥ {{ coreMinVisits || 'M' }} 次才升级为核心患者；仅在 {{ visitStatPeriod || 'N' }} 个月前就诊 5 次、近 {{ visitStatPeriod || 'N' }} 个月未就诊的，仍为普通患者。
        </div>
      </div>

      <!-- ===== 第 2 层：核心患者 → 重点患者 ===== -->
      <div class="layer-block layer-block-focus">
        <div class="layer-header">
          <span class="layer-badge layer-badge-focus">2</span>
          <span class="layer-title">第2层：核心患者 → 重点患者（慢病管理）</span>
        </div>
        <!-- ★ 动态绑定 focusPeriod -->
        <div class="layer-desc">
          在核心患者基础上，统计近 {{ focusPeriod || 'N' }} 个月内的治疗与用药数据，满足以下任一条件即升级为重点患者。
        </div>

        <div class="param-grid">
          <div v-if="hasField('focusPeriod')" class="param-item">
            <span class="param-label">
              <span
                class="status-dot"
                :class="isFieldDisabled('focusPeriod') ? 'off' : 'on'"
                :title="iconTooltip('focusPeriod')"
                @click="handleToggleFromDot('focusPeriod')"
              ></span>
              重点判定周期
            </span>
            <div class="param-input-wrap">
              <el-input-number
                v-model="focusPeriod"
                :min="1"
                :step="1"
                size="small"
                controls-position="right"
                :disabled="isFieldDisabled('focusPeriod')"
                @change="handleFieldChange('focusPeriod')"
              ></el-input-number>
              <span class="param-unit">个月</span>
            </div>
            <!-- ★ 动态绑定 focusPeriod -->
            <div class="hint-block">在核心患者基础上，近 {{ focusPeriod || 'N' }} 个月内判定是否满足慢病管理规则</div>
          </div>
        </div>

        <!-- 条件一 -->
        <div class="sub-condition">
          <div class="sub-condition-title">条件一：规律治疗型（重复治疗）</div>
          <div class="param-grid">
            <div v-if="hasField('focusCourseThreshold')" class="param-item">
              <span class="param-label">
                <span
                  class="status-dot"
                  :class="isFieldDisabled('focusCourseThreshold') ? 'off' : 'on'"
                  :title="iconTooltip('focusCourseThreshold')"
                  @click="handleToggleFromDot('focusCourseThreshold')"
                ></span>
                同种治疗方式/药品开具次数阈值
              </span>
              <div class="param-input-wrap">
                <el-input-number
                  v-model="focusCourseThreshold"
                  :min="1"
                  :step="1"
                  size="small"
                  controls-position="right"
                  :disabled="isFieldDisabled('focusCourseThreshold')"
                  @change="handleFieldChange('focusCourseThreshold')"
                ></el-input-number>
                <span class="param-unit">次</span>
              </div>
              <div class="hint-block">条件一（规律治疗型·重复治疗维度）：判定周期内「同一治疗方式」或「同一药品」的规律开具次数，满足其一即达标（已去掉月均就诊次数）</div>
            </div>
          </div>
          <div class="sub-condition-note">
            判定周期内，「同一治疗方式」的规律开具次数 <span class="hl-or">或</span>「同一药品」的规律开具次数，满足其一即达标（已去掉月均就诊次数维度）。
          </div>
        </div>

        <!-- 条件二 -->
        <div class="sub-condition">
          <div class="sub-condition-title">条件二：长期用药型（重复用药）</div>
          <div class="param-grid">
            <div v-if="hasField('focusMonthThreshold')" class="param-item">
              <span class="param-label">
                <span
                  class="status-dot"
                  :class="isFieldDisabled('focusMonthThreshold') ? 'off' : 'on'"
                  :title="iconTooltip('focusMonthThreshold')"
                  @click="handleToggleFromDot('focusMonthThreshold')"
                ></span>
                连续用药月数阈值
              </span>
              <div class="param-input-wrap">
                <el-input-number
                  v-model="focusMonthThreshold"
                  :min="1"
                  :step="1"
                  size="small"
                  controls-position="right"
                  :disabled="isFieldDisabled('focusMonthThreshold')"
                  @change="handleFieldChange('focusMonthThreshold')"
                ></el-input-number>
                <span class="param-unit">个月</span>
              </div>
              <div class="hint-block">条件二（长期用药型·重复用药维度）：连续用药月数 ≥ 阈值，衡量重复用药</div>
            </div>
          </div>
          <div class="sub-condition-note">
            同一药品连续用药月数 ≥ 阈值（已去掉单次开药天数参数）。
          </div>
        </div>

        <!-- 手动标记说明 -->
        <div class="manual-tag-tip">
          <i class="el-icon-star-off"></i>
          手动标记：医生可将核心患者手动标记为重点患者，需填写标记原因，可随时移除；手动标记不受上述自动规则限制。
        </div>
      </div>

      <!-- ===== 第 3 层：核心/重点患者 → 流失核心患者 ===== -->
      <div class="layer-block layer-block-lost">
        <div class="layer-header">
          <span class="layer-badge layer-badge-lost">3</span>
          <span class="layer-title">第3层：核心/重点患者 → 流失核心患者</span>
        </div>
        <div class="layer-desc">
          核心患者或重点患者最后就诊日期距当前超过该天数，判定为流失核心患者；重新就诊后自动恢复到流失前的分类层级。
        </div>
        <div class="param-grid">
          <div v-if="hasField('lostDays')" class="param-item">
            <span class="param-label">
              <span
                class="status-dot"
                :class="isFieldDisabled('lostDays') ? 'off' : 'on'"
                :title="iconTooltip('lostDays')"
                @click="handleToggleFromDot('lostDays')"
              ></span>
              最后就诊超期天数
            </span>
            <div class="param-input-wrap">
              <el-input-number
                v-model="lostDays"
                :min="1"
                :step="1"
                size="small"
                controls-position="right"
                :disabled="isFieldDisabled('lostDays')"
                @change="handleFieldChange('lostDays')"
              ></el-input-number>
              <span class="param-unit">天</span>
            </div>
            <div class="hint-block">核心/重点患者最后就诊距今超期即判断为「流失核心患者」；重新就诊恢复到流失前层级</div>
          </div>
        </div>
        <div class="layer-example">
          重新就诊后恢复到流失前层级：若流失前为重点患者，重新就诊时仍需符合慢病规则（条件一/条件二）否则降为核心患者；若流失前为核心患者则恢复为核心患者。
        </div>
      </div>
    </el-card>

    <!-- ============ 统计分析参数 ============ -->
    <el-card class="section-card stats-card" shadow="hover">
      <div slot="header" class="card-header">
        <div class="header-left">
          <i class="el-icon-s-data card-icon"><span class="card-title">统计分析参数</span></i>
          <span class="card-tip">用于患者数据看板的图表统计。</span>
        </div>
      </div>

      <!-- 年龄段划分 -->
      <div class="stats-group">
        <div class="stats-group-title">
          <span class="dot-blue"></span>
          年龄段划分（老年 = 60 岁及以上自动）
        </div>
        <div class="stats-grid">
          <div class="param-item">
            <span class="param-label">
              <span
                class="status-dot"
                :class="isFieldDisabled('pediatricAgeLimit') ? 'off' : 'on'"
                :title="iconTooltip('pediatricAgeLimit')"
                @click="handleToggleFromDot('pediatricAgeLimit')"
              ></span>
              儿科年龄上限
            </span>
            <div class="param-input-wrap">
              <el-input-number
                v-model="pediatricAgeLimit"
                :min="1"
                :step="1"
                size="small"
                controls-position="right"
                :disabled="true"
                @change="handleFieldChange('pediatricAgeLimit')"
              ></el-input-number>
              <span class="param-unit">岁</span>
            </div>
            <div class="hint-block">年龄段划分：0 ~ 该值为儿科</div>
          </div>

          <div class="param-item">
            <span class="param-label">
              <span
                class="status-dot"
                :class="isFieldDisabled('youthAgeLimit') ? 'off' : 'on'"
                :title="iconTooltip('youthAgeLimit')"
                @click="handleToggleFromDot('youthAgeLimit')"
              ></span>
              青年年龄上限
            </span>
            <div class="param-input-wrap">
              <el-input-number
                v-model="youthAgeLimit"
                :min="1"
                :step="1"
                size="small"
                controls-position="right"
                :disabled="true"
                @change="handleFieldChange('youthAgeLimit')"
              ></el-input-number>
              <span class="param-unit">岁</span>
            </div>
            <div class="hint-block">年龄段划分：儿科上限 +1 ~ 该值为青年</div>
          </div>

          <div class="param-item">
            <span class="param-label">
              <span
                class="status-dot"
                :class="isFieldDisabled('middleAgeLimit') ? 'off' : 'on'"
                :title="iconTooltip('middleAgeLimit')"
                @click="handleToggleFromDot('middleAgeLimit')"
              ></span>
              中年年龄上限
            </span>
            <div class="param-input-wrap">
              <el-input-number
                v-model="middleAgeLimit"
                :min="1"
                :step="1"
                size="small"
                controls-position="right"
                :disabled="true"
                @change="handleFieldChange('middleAgeLimit')"
              ></el-input-number>
              <span class="param-unit">岁</span>
            </div>
            <div class="hint-block">年龄段划分：青年上限 +1 ~ 该值为中年，之上为老年</div>
          </div>
        </div>
      </div>

      <!-- 客单价区间 -->
      <div class="stats-group">
        <div class="stats-group-title">
          <span class="dot-blue"></span>
          客单价区间（200 元以上自动归为最高档）
        </div>
        <div class="stats-grid">
          <div v-if="hasField('priceRange1')" class="param-item">
            <span class="param-label">
              <span
                class="status-dot"
                :class="isFieldDisabled('priceRange1') ? 'off' : 'on'"
                :title="iconTooltip('priceRange1')"
                @click="handleToggleFromDot('priceRange1')"
              ></span>
              客单价区间1上限
            </span>
            <div class="param-input-wrap">
              <el-input-number
                v-model="priceRange1"
                :min="1"
                :step="10"
                size="small"
                controls-position="right"
                :disabled="isFieldDisabled('priceRange1')"
                @change="handleFieldChange('priceRange1')"
              ></el-input-number>
              <span class="param-unit">元</span>
            </div>
            <div class="hint-block">客单价区间：0 ~ 该值</div>
          </div>

          <div v-if="hasField('priceRange2')" class="param-item">
            <span class="param-label">
              <span
                class="status-dot"
                :class="isFieldDisabled('priceRange2') ? 'off' : 'on'"
                :title="iconTooltip('priceRange2')"
                @click="handleToggleFromDot('priceRange2')"
              ></span>
              客单价区间2上限
            </span>
            <div class="param-input-wrap">
              <el-input-number
                v-model="priceRange2"
                :min="1"
                :step="10"
                size="small"
                controls-position="right"
                :disabled="isFieldDisabled('priceRange2')"
                @change="handleFieldChange('priceRange2')"
              ></el-input-number>
              <span class="param-unit">元</span>
            </div>
            <div class="hint-block">客单价区间：区间1上限 ~ 该值</div>
          </div>

          <div v-if="hasField('priceRange3')" class="param-item">
            <span class="param-label">
              <span
                class="status-dot"
                :class="isFieldDisabled('priceRange3') ? 'off' : 'on'"
                :title="iconTooltip('priceRange3')"
                @click="handleToggleFromDot('priceRange3')"
              ></span>
              客单价区间3上限
            </span>
            <div class="param-input-wrap">
              <el-input-number
                v-model="priceRange3"
                :min="1"
                :step="10"
                size="small"
                controls-position="right"
                :disabled="isFieldDisabled('priceRange3')"
                @change="handleFieldChange('priceRange3')"
              ></el-input-number>
              <span class="param-unit">元</span>
            </div>
            <div class="hint-block">客单价区间：区间2上限 ~ 该值，之上为最高档</div>
          </div>
        </div>
      </div>

      <!-- 高频项目/药品统计周期 -->
      <div class="stats-group">
        <div class="stats-group-title">
          <span class="dot-blue"></span>
          高频项目/药品统计周期
        </div>
        <div class="stats-grid">
          <div v-if="hasField('followStatsPeriod')" class="param-item">
            <span class="param-label">
              <span
                class="status-dot"
                :class="isFieldDisabled('followStatsPeriod') ? 'off' : 'on'"
                :title="iconTooltip('followStatsPeriod')"
                @click="handleToggleFromDot('followStatsPeriod')"
              ></span>
              高频项目/药品统计周期
            </span>
            <div class="param-input-wrap">
              <el-input-number
                v-model="followStatsPeriod"
                :min="1"
                :step="1"
                size="small"
                controls-position="right"
                :disabled="isFieldDisabled('followStatsPeriod')"
                @change="handleFieldChange('followStatsPeriod')"
              ></el-input-number>
              <span class="param-unit">天</span>
            </div>
            <div class="hint-block">高频处置/药品 TOP10 统计窗口</div>
          </div>
        </div>
      </div>
    </el-card>

    <!-- 底部操作按钮 -->
    <div class="params-card-footer">
      <el-button
        class="btn-outline"
        size="medium"
        icon="el-icon-refresh"
        @click="handleReset"
      >恢复默认</el-button>
      <el-button
        class="btn-primary"
        size="medium"
        icon="el-icon-xmdz"
        :disabled="!hasChanges"
        @click="handleSave"
      >保存配置</el-button>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts';
import { getSystemList, saveSystemData, updateSystemData } from "@/api/systemParameter/systemParameter";
import { docphrpage } from "@/api/Outpatient/Healthrecords";

// 每次 updateSystemData 的最小 loading 时长（毫秒）
const MIN_LOADING_MS = 1800;
// 消息提示展示时长（毫秒）
const MESSAGE_DURATION = 1800;

// 患者类型映射（颜色与品牌规范一致）
const PHR_TYPE_MAP = {
  1: { key: 'normal',   label: '普通患者',     color: '#52c41a' }, // 绿
  2: { key: 'core',     label: '核心患者',     color: '#1890ff' }, // 蓝
  3: { key: 'focus',    label: '重点患者',     color: '#f5222d' }, // 红
  4: { key: 'lostCore', label: '流失核心患者', color: '#fa8c16' }  // 橙
};

export default {
  name: 'PatientSystemParams',
  data() {
    return {
      stats: {
        focus: 0,
        core: 0,
        lostCore: 0,
        normal: 0
      },
      // 第 1 层
      visitStatPeriod: 2,
      coreMinVisits: 3,
      // 第 2 层
      focusPeriod: 6,
      focusCourseThreshold: 4,
      focusMonthThreshold: 3,
      // 第 3 层
      lostDays: 180,
      // 统计分析参数
      pediatricAgeLimit: 14,
      youthAgeLimit: 35,
      middleAgeLimit: 59,
      priceRange1: 50,
      priceRange2: 100,
      priceRange3: 200,
      followStatsPeriod: 90,

      activeIndex: null,
      hoverIndex: null,

      loading: false,
      fieldMap: {
        visitStatPeriod: '核心患者就诊统计周期',
        coreMinVisits: '最小就诊次数',
        focusPeriod: '重点患者判定周期',
        focusCourseThreshold: '重点患者同种治疗开具次数阈值',
        focusMonthThreshold: '重点患者连续用药月数阈值',
        lostDays: '流失无就诊天数',
        pediatricAgeLimit: '儿科年龄上限',
        youthAgeLimit: '青年年龄上限',
        middleAgeLimit: '中年年龄上限',
        priceRange1: '客单价区间1上限',
        priceRange2: '客单价区间2上限',
        priceRange3: '客单价区间3上限',
        followStatsPeriod: '高频项目/药品统计周期'
      },
      fieldMatchMap: {
        visitStatPeriod:      { param: 'core_patient_visit_period' },
        focusPeriod:          { param: 'key_patient_period' },
        focusCourseThreshold: { param: 'key_patient_treatment_count' },
        focusMonthThreshold:  { param: 'key_patient_medication_months' }
      },
      fieldMeta: {},
      originalSnapshot: '',

      donutChartInstance: null
    };
  },
  computed: {
    totalSamples() {
      return this.stats.focus + this.stats.core + this.stats.lostCore + this.stats.normal;
    },
    cardList() {
      return [
        { key: 'normal',   label: '普通患者',     count: this.stats.normal,   color: PHR_TYPE_MAP[1].color, phrType: 1, extra: '' },
        { key: 'core',     label: '核心患者',     count: this.stats.core,     color: PHR_TYPE_MAP[2].color, phrType: 2, extra: '' },
        { key: 'focus',    label: '重点患者',     count: this.stats.focus,    color: PHR_TYPE_MAP[3].color, phrType: 3, extra: '' },
        { key: 'lostCore', label: '流失核心患者', count: this.stats.lostCore, color: PHR_TYPE_MAP[4].color, phrType: 4, extra: '' }
      ];
    },
    /* 环形图中心文案 */
    activePhrLabel() {
      if (this.activeIndex === null) return '全部患者';
      const card = this.cardList[this.activeIndex];
      return card ? card.label : '全部患者';
    },
    /* 环形图中心人数：未选中 → 全部；选中 → 该分类人数 */
    currentCenterCount() {
      if (this.activeIndex === null) return this.totalSamples;
      const card = this.cardList[this.activeIndex];
      return card ? card.count : 0;
    },
    currentSnapshot() {
      return JSON.stringify({
        visitStatPeriod: this.visitStatPeriod,
        coreMinVisits: this.coreMinVisits,
        focusPeriod: this.focusPeriod,
        focusCourseThreshold: this.focusCourseThreshold,
        focusMonthThreshold: this.focusMonthThreshold,
        lostDays: this.lostDays,
        pediatricAgeLimit: this.pediatricAgeLimit,
        youthAgeLimit: this.youthAgeLimit,
        middleAgeLimit: this.middleAgeLimit,
        priceRange1: this.priceRange1,
        priceRange2: this.priceRange2,
        priceRange3: this.priceRange3,
        followStatsPeriod: this.followStatsPeriod
      });
    },
    hasChanges() {
      return this.currentSnapshot !== this.originalSnapshot;
    }
  },
  async mounted() {
    await Promise.all([
      this.fetchSystemList(),
      this.loadPhrTypeCounts()
    ]);
    this.$nextTick(() => {
      this.originalSnapshot = this.currentSnapshot;
      this.initDonutChart();
    });
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    if (this.donutChartInstance) {
      this.donutChartInstance.dispose();
      this.donutChartInstance = null;
    }
  },
  methods: {
    /* ============================================================
     *  ① 拉取各分类患者数量
     * ============================================================ */
    async loadPhrTypeCounts() {
      const requests = [1, 2, 3, 4].map(type => {
        const params = {
          name: '',
          pageNum: 1,
          pageSize: 1,
          phrType: type
        };
        return docphrpage(params).catch(err => {
          console.error(`[docphrpage] phrType=${type} 请求失败:`, err);
          return { data: {} };
        });
      });

      try {
        const results = await Promise.all(requests);
        const newStats = { normal: 0, core: 0, focus: 0, lostCore: 0 };

        results.forEach((res, i) => {
          const phrType = i + 1;
          const key = PHR_TYPE_MAP[phrType].key;
          const data = (res && res.data) || {};
          newStats[key] = Number(
            data.totalSize != null ? data.totalSize
            : data.total != null ? data.total
            : data.totalElements != null ? data.totalElements
            : (Array.isArray(data.content) ? data.content.length : 0)
          ) || 0;
        });

        this.stats = {
          normal: newStats.normal,
          core: newStats.core,
          focus: newStats.focus,
          lostCore: newStats.lostCore
        };

        this.$nextTick(() => this.initDonutChart());
      } catch (err) {
        console.error('加载患者分类数量失败:', err);
      }
    },

    /* ============================================================
     *  ② 点击卡片：走马灯 + 立即刷新图表 + 按 phrType 再次调用 docphrpage
     * ============================================================ */
    async handleCardClick(index) {
      if (this.activeIndex === index) {
        this.resetCardSelection();
        return;
      }

      this.activeIndex = index;
      this.$nextTick(() => this.initDonutChart());

      const card = this.cardList[index];
      if (!card) return;

      await this.fetchPhrTypeDetail(card.phrType);
    },

    async fetchPhrTypeDetail(phrType) {
      const params = {
        name: '',
        pageNum: 1,
        pageSize: 20
      };
      if (phrType !== null && phrType !== undefined && phrType !== '') {
        params.phrType = phrType;
      }

      try {
        const res = await docphrpage(params);
        const data = (res && res.data) || {};
        const newCount = Number(
          data.totalSize != null ? data.totalSize
          : data.total != null ? data.total
          : data.totalElements != null ? data.totalElements
          : (Array.isArray(data.content) ? data.content.length : 0)
        ) || 0;

        const key = PHR_TYPE_MAP[phrType] && PHR_TYPE_MAP[phrType].key;
        if (key) {
          this.stats = { ...this.stats, [key]: newCount };
          this.$nextTick(() => this.initDonutChart());
        }
      } catch (err) {
        console.error(`[docphrpage] phrType=${phrType} 详情请求失败:`, err);
      }
    },

    resetCardSelection() {
      this.activeIndex = null;
      this.$nextTick(() => this.initDonutChart());
    },

    /* ============================================================
     *  系统参数：拉取 / 匹配
     * ============================================================ */
    async fetchSystemList() {
      try {
        const res = await getSystemList();
        const rawList = Array.isArray(res && res.data) ? res.data : [];
        const patientList = rawList.filter(item => item && Number(item.cate) === 6);

        const paramToKey = {};
        const nameToKey = {};
        Object.keys(this.fieldMap).forEach(key => {
          const rule = this.fieldMatchMap[key];
          if (rule && rule.param) {
            paramToKey[rule.param] = key;
          } else {
            nameToKey[this.fieldMap[key]] = key;
          }
        });

        const metaMap = {};
        patientList.forEach(item => {
          const key = paramToKey[item.param] || nameToKey[item.name];
          if (!key) return;

          metaMap[key] = {
            id: item.id,
            name: item.name,
            param: item.param,
            state: item.state,
            value: item.value
          };

          if (item.state === 1) {
            if (typeof this[key] === 'number') {
              const num = Number(item.value);
              this[key] = isNaN(num) ? this[key] : num;
            } else {
              this[key] = item.value;
            }
          }
        });
        this.fieldMeta = metaMap;
      } catch (err) {
        console.error('[getSystemList] 拉取失败:', err);
      }
    },

    /* ============ 字段状态判断 ============ */
    hasField(key) {
      return !!this.fieldMeta[key];
    },
    isFieldDisabled(key) {
      return this.hasField(key) && this.fieldMeta[key].state === 0;
    },
    fieldLabel(key) {
      return this.fieldMap[key] || key;
    },
    iconTooltip(key) {
      return this.isFieldDisabled(key) ? '点击启用' : '点击禁用';
    },
    toggleTitle(key) {
      const action = this.isFieldDisabled(key) ? '启用' : '禁用';
      return `确认${action}：${this.fieldMap[key]}吗？`;
    },

    handleToggleFromDot(key) {
      if (!this.hasField(key)) return;
      const action = this.isFieldDisabled(key) ? '启用' : '禁用';
      this.$confirm(
        `确认${action}：${this.fieldMap[key]}吗？`,
        '系统提示',
        {
          confirmButtonText: '确认',
          cancelButtonText: '取消',
          type: 'warning'
        }
      ).then(() => {
        this.handleToggleField(key);
      }).catch(() => {});
    },

    showSuccessMessage(msg) {
      this.$nextTick(() => {
        this.$message({
          message: msg,
          type: 'success',
          duration: MESSAGE_DURATION
        });
      });
    },

    showErrorBox(message) {
      this.$nextTick(() => {
        this.$alert(message || '操作失败，请稍后重试', '系统提示：', {
          confirmButtonText: '我已知晓',
          type: 'error',
          center: true,
          showClose: false,
          customClass: 'system-alert-box'
        }).catch(() => {});
      });
    },

    async handleFieldChange(key) {
      const meta = this.fieldMeta[key];
      if (!meta) return;

      const newValue = this[key];
      const oldValue = meta.value;
      const normalize = v => (typeof v === 'boolean' ? (v ? '1' : '0') : String(v));
      if (normalize(newValue) === normalize(oldValue)) {
        return;
      }

      const startTime = Date.now();
      this.loading = true;

      let successMsg = '';
      let errorMsg = '';

      try {
        const res = await updateSystemData({
          id: meta.id,
          name: meta.name,
          param: meta.param,
          state: meta.state,
          value: typeof newValue === 'boolean' ? (newValue ? 1 : 0) : newValue
        });

        if (res && res.code === 200) {
          successMsg = `「${meta.name}」修改成功`;
          await this.fetchSystemList();
          this.originalSnapshot = this.currentSnapshot;
        } else {
          errorMsg = (res && res.message) || `「${meta.name}」修改失败`;
          await this.fetchSystemList();
        }
      } catch (err) {
        errorMsg = (err && err.message) || `「${meta.name}」修改失败`;
        console.error('[updateSystemData] 修改失败:', err);
        await this.fetchSystemList();
      } finally {
        await this.ensureMinLoading(startTime);
        this.loading = false;
        if (successMsg) {
          this.showSuccessMessage(successMsg);
        } else if (errorMsg) {
          this.showErrorBox(errorMsg);
        }
      }
    },

    async handleToggleField(key) {
      const meta = this.fieldMeta[key];
      if (!meta) return;

      const newState = meta.state === 1 ? 0 : 1;
      const startTime = Date.now();
      this.loading = true;

      let successMsg = '';
      let errorMsg = '';

      try {
        const res = await updateSystemData({
          id: meta.id,
          name: meta.name,
          param: meta.param,
          state: newState,
          value: meta.value
        });

        if (res && res.code === 200) {
          successMsg = newState === 1 ? '启用成功' : '禁用成功';
          await this.fetchSystemList();
          this.originalSnapshot = this.currentSnapshot;
        } else {
          errorMsg = (res && res.message) || (newState === 1 ? '启用失败' : '禁用失败');
          await this.fetchSystemList();
        }
      } catch (err) {
        errorMsg = (err && err.message) || (newState === 1 ? '启用失败' : '禁用失败');
        console.error('[updateSystemData] 切换状态失败:', err);
      } finally {
        await this.ensureMinLoading(startTime);
        this.loading = false;
        if (successMsg) {
          this.showSuccessMessage(successMsg);
        } else if (errorMsg) {
          this.showErrorBox(errorMsg);
        }
      }
    },

    async ensureMinLoading(startTime) {
      const elapsed = Date.now() - startTime;
      if (elapsed < MIN_LOADING_MS) {
        await new Promise(resolve => setTimeout(resolve, MIN_LOADING_MS - elapsed));
      }
    },

    async handleSave() {
      const payload = Object.keys(this.fieldMap)
        .filter(key => this.hasField(key))
        .map(key => {
          const meta = this.fieldMeta[key] || {};
          return {
            id: meta.id || 0,
            name: meta.name || this.fieldMap[key],
            param: meta.param || key,
            state: meta.state != null ? meta.state : 1,
            value: typeof this[key] === 'boolean' ? (this[key] ? 1 : 0) : this[key]
          };
        });

      const startTime = Date.now();
      this.loading = true;

      let successMsg = '';
      let errorMsg = '';

      try {
        const res = await saveSystemData(payload);
        if (res && res.code === 200) {
          successMsg = '配置已保存';
          this.originalSnapshot = this.currentSnapshot;
        } else {
          errorMsg = (res && res.message) || '保存失败';
        }
      } catch (err) {
        errorMsg = (err && err.message) || '保存失败';
        console.error('[saveSystemData] 保存失败:', err);
      } finally {
        await this.ensureMinLoading(startTime);
        this.loading = false;
        if (successMsg) {
          this.showSuccessMessage(successMsg);
        } else if (errorMsg) {
          this.showErrorBox(errorMsg);
        }
      }
    },

    handleReset() {
      this.$confirm(
        '确认将当前配置恢复为默认值吗？此操作不会立即提交到服务器，需要再次点击「保存配置」才会生效。',
        '系统提示',
        {
          confirmButtonText: '确认恢复',
          cancelButtonText: '取消',
          type: 'warning'
        }
      ).then(() => {
        this.visitStatPeriod = 2;
        this.coreMinVisits = 3;
        this.focusPeriod = 6;
        this.focusCourseThreshold = 4;
        this.focusMonthThreshold = 3;
        this.lostDays = 180;
        this.pediatricAgeLimit = 14;
        this.youthAgeLimit = 35;
        this.middleAgeLimit = 59;
        this.priceRange1 = 50;
        this.priceRange2 = 100;
        this.priceRange3 = 200;
        this.followStatsPeriod = 90;
        this.$message({ message: '已恢复默认值，请点击「保存配置」提交', type: 'info', duration: MESSAGE_DURATION });
      }).catch(() => {});
    },

    /* ============================================================
     *  环形图：颜色与选中状态同步
     * ============================================================ */
    initDonutChart() {
      const dom = this.$refs.donutChart;
      if (!dom) return;

      if (this.donutChartInstance) {
        try { this.donutChartInstance.clear(); } catch (e) {}
        try { this.donutChartInstance.dispose(); } catch (e) {}
        this.donutChartInstance = null;
      }
      this.donutChartInstance = echarts.init(dom);

      const fullList = [
        { value: this.stats.normal,   name: '普通患者',     color: PHR_TYPE_MAP[1].color },
        { value: this.stats.core,     name: '核心患者',     color: PHR_TYPE_MAP[2].color },
        { value: this.stats.focus,    name: '重点患者',     color: PHR_TYPE_MAP[3].color },
        { value: this.stats.lostCore, name: '流失核心患者', color: PHR_TYPE_MAP[4].color }
      ];

      let data;
      if (this.activeIndex !== null) {
        const sel = fullList[this.activeIndex];
        const v = sel.value > 0 ? sel.value : 1;
        data = [{
          value: v,
          name: sel.name,
          itemStyle: { color: sel.color }
        }];
      } else {
        data = fullList.map(d => ({
          value: d.value,
          name: d.name,
          itemStyle: { color: d.color }
        }));
      }

      const option = {
        tooltip: {
          trigger: 'item',
          formatter: '{b}: {c} 人 ({d}%)'
        },
        series: [
          {
            type: 'pie',
            radius: ['58%', '82%'],
            center: ['50%', '50%'],
            avoidLabelOverlap: false,
            itemStyle: {
              borderColor: '#fff',
              borderWidth: 2
            },
            label: { show: false },
            labelLine: { show: false },
            emphasis: { scale: false },
            data: data
          }
        ]
      };

      // 全量覆盖旧配置，避免颜色残留
      this.donutChartInstance.setOption(option, true);
    },

    handleResize() {
      if (this.donutChartInstance) {
        this.donutChartInstance.resize();
      }
    }
  }
};
</script>

<style lang="less" scoped>
/* ==================== 页面整体 ==================== */
.patient-system-params {
  max-width: 100%;
  margin: 0 auto;
  padding: 20px 24px;
  background: #f0f2f5;
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang SC", "Helvetica Neue", Arial, sans-serif;
}

/* ==================== 页面标题 ==================== */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;

  .header-left { flex: 1; }

  .page-title {
    font-size: 22px;
    font-weight: 700;
    color: #1d2129;
    margin: 0 0 8px 0;
    letter-spacing: 0.3px;
  }

  .page-subtitle {
    font-size: 13px;
    color: #86909c;
    margin: 0;
    line-height: 1.5;
  }

  .header-right {
    flex-shrink: 0;
    display: flex;
    gap: 10px;
    padding-top: 4px;
  }

  .reset-btn {
    background: #fff;
    border: 1px solid #d9d9d9;
    border-radius: 8px;
    color: #4e5969;
    padding: 9px 20px;
    font-weight: 500;
    transition: all 0.2s;

    &:hover {
      background: #f5f7fa;
      border-color: #1890ff;
      color: #1890ff;
    }
  }

  .save-btn {
    background: #1890ff;
    border: none;
    border-radius: 8px;
    color: #fff;
    padding: 9px 22px;
    font-weight: 500;
    box-shadow: 0 2px 6px rgba(24, 144, 255, 0.25);
    transition: all 0.2s;

    &:hover:not(.is-disabled):not(:disabled) {
      background: #40a9ff;
      box-shadow: 0 3px 10px rgba(24, 144, 255, 0.35);
    }

    &.is-disabled,
    &:disabled {
      background: #b6cad7 !important;
      color: rgba(255, 255, 255, 0.85) !important;
      border: none !important;
      box-shadow: none !important;
      cursor: not-allowed;
    }
  }
}

/* ==================== 通用卡片 ==================== */
.section-card {
  margin-bottom: 16px;
  border-radius: 12px;
  border: none;

  /deep/ .el-card__header {
    padding: 18px 22px 12px;
    border-bottom: none;
  }

  /deep/ .el-card__body {
    padding: 0 22px 20px;
  }

  .card-header {
    display: flex;
    align-items: flex-start;
    .header-left {
      flex: 1;
      display: flex;
      flex-direction: column;
      .card-icon {
        font-size: 18px;
        color: #1890ff;
        margin-bottom: 6px;
      }
      .card-title {
        font-size: 17px;
        font-weight: 600;
        padding-left: 10px;
        color: #1d2129;
        line-height: 1.4;
      }
      .card-tip {
        font-size: 12px;
        color: #86909c;
        margin-top: 4px;
        line-height: 1.5;
        display: block;

        .reset-tip {
          margin-left: 6px;
          color: #1890ff;
          cursor: pointer;

          &:hover { text-decoration: underline; }
        }
      }
    }
  }
}

/* ==================== 规则实时预览 ==================== */
.preview-card {
  .preview-body {
    display: flex;
    align-items: center;
    gap: 32px;
    padding: 8px 4px 4px;
    min-height: 220px;
  }

  .donut-wrapper {
    flex: 0 0 180px;
    width: 180px;
    height: 180px;
    position: relative;

    .donut-chart {
      width: 100%;
      height: 100%;
    }

    .donut-center {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      text-align: center;
      pointer-events: none;
      width: 110px;

      .donut-center-num {
        font-size: 16px;
        font-weight: 700;
        color: #1d2129;
        line-height: 1.2;
        white-space: nowrap;
        transition: all 0.3s ease;

        &.is-all { color: #4e5969; }
      }

      .donut-center-label {
        font-size: 12px;
        color: #86909c;
        margin-top: 4px;
      }
    }
  }

  .stat-cards {
    flex: 1;
    display: flex;
    gap: 16px;
    min-width: 0;
    align-items: stretch;
    position: relative;
    transition: all 0.55s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .stat-card-item {
    flex: 1 1 0;
    min-width: 0;
    background: #fafbfc;
    border: 1px solid #e5e6eb;
    border-radius: 10px;
    padding: 16px 18px;
    cursor: pointer;
    transition: all 0.55s cubic-bezier(0.4, 0, 0.2, 1);
    overflow: hidden;
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;

    &:hover:not(.active):not(.dimmed) {
      border-color: #1890ff;
      box-shadow: 0 4px 12px rgba(24, 144, 255, 0.12);
      transform: translateY(-2px);
    }

    .stat-card-header {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 10px;
      white-space: nowrap;
      transition: all 0.4s ease;
    }

    .stat-dot {
      display: inline-block;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      flex-shrink: 0;
      transition: all 0.4s ease;
    }

    .stat-card-label {
      font-size: 13px;
      color: #4e5969;
      line-height: 1.4;
      transition: all 0.4s ease;
    }

    .stat-card-value {
      font-size: 28px;
      font-weight: 700;
      color: #1d2129;
      line-height: 1.1;
      white-space: nowrap;
      transition: all 0.4s ease;

      .unit {
        font-size: 13px;
        font-weight: 400;
        color: #86909c;
        margin-left: 4px;
      }
    }

    .stat-card-extra {
      font-size: 11px;
      color: #86909c;
      margin-top: 6px;
      line-height: 1.4;
      transition: all 0.4s ease;
    }

    &.active {
      flex: 3 1 0;
      background: #f0f7ff;
      border: 2px solid #1890ff;
      box-shadow: 0 12px 32px rgba(24, 144, 255, 0.24);
      transform: scale(1.03);
      z-index: 3;
      cursor: pointer;
      padding: 22px 26px;

      .stat-card-header {
        gap: 10px;
        margin-bottom: 14px;

        .stat-dot {
          width: 12px;
          height: 12px;
          box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.15);
        }

        .stat-card-label {
          font-size: 16px;
          font-weight: 600;
          color: #1d2129;
        }
      }

      .stat-card-value {
        font-size: 42px;

        .unit { font-size: 16px; }
      }

      .stat-card-close-tip {
        position: absolute;
        right: 12px;
        bottom: 10px;
        font-size: 11px;
        color: #1890ff;
        display: flex;
        align-items: center;
        gap: 3px;
        opacity: 0.75;

        i { font-size: 12px; }
      }
    }

    &.dimmed {
      flex: 0.4 1 0;
      opacity: 0.28;
      filter: grayscale(0.4);
      transform: scale(0.9);

      .stat-card-header {
        .stat-card-label { font-size: 12px; }
      }

      .stat-card-value {
        font-size: 20px;

        .unit { font-size: 11px; }
      }

      .stat-card-extra { display: none; }
    }
  }
}

/* ==================== 患者分类递进参数 ==================== */
.params-card {
  .flow-tip {
    background: #fafbfc;
    border: 1px solid #e5e6eb;
    border-radius: 10px;
    padding: 14px 18px;
    margin-bottom: 20px;

    .flow-row {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 8px;
      margin-bottom: 10px;

      &:last-child { margin-bottom: 0; }

      &.flow-row-note {
        padding-top: 6px;
        border-top: 1px dashed #e5e6eb;
        margin-top: 4px;
      }
    }

    .flow-tag {
      display: inline-block;
      padding: 3px 10px;
      border-radius: 4px;
      font-size: 12px;
      font-weight: 500;

      &.flow-normal {
        background: #f6ffed;
        color: #52c41a;
        border: 1px solid #b7eb8f;
      }
      &.flow-core {
        background: #e6f7ff;
        color: #1890ff;
        border: 1px solid #91d5ff;
      }
      &.flow-focus {
        background: #fff1f0;
        color: #f5222d;
        border: 1px solid #ffa39e;
      }
      &.flow-lost {
        background: #fff7e6;
        color: #fa8c16;
        border: 1px solid #ffd591;
      }
    }

    .flow-arrow {
      font-size: 12px;
      color: #86909c;
      margin: 0 2px;
    }

    .flow-text {
      font-size: 12px;
      color: #4e5969;
    }

    .flow-note {
      font-size: 12px;
      color: #86909c;
      font-style: italic;
    }
  }

  .layer-block {
    border-radius: 10px;
    padding: 18px 20px 20px;
    margin-bottom: 16px;
    background: #f0f7ff;
    border: 1px solid #d6e8ff;

    &.layer-block-focus {
      background: #fff5f5;
      border-color: #ffd9d9;
    }

    &.layer-block-lost {
      background: #fff9ee;
      border-color: #ffe0b7;
    }

    .layer-header {
      display: flex;
      align-items: center;
      margin-bottom: 10px;

      .layer-badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        background: #1890ff;
        color: #fff;
        font-size: 13px;
        font-weight: 700;
        margin-right: 10px;
        flex-shrink: 0;
        box-shadow: 0 2px 6px rgba(24, 144, 255, 0.35);

        &.layer-badge-focus {
          background: #f5222d;
          box-shadow: 0 2px 6px rgba(245, 34, 45, 0.35);
        }
        &.layer-badge-lost {
          background: #fa8c16;
          box-shadow: 0 2px 6px rgba(250, 140, 22, 0.35);
        }
      }

      .layer-title {
        font-size: 14px;
        font-weight: 600;
        color: #1d2129;
        line-height: 1.4;
      }
    }

    .layer-desc {
      font-size: 12px;
      color: #606266;
      line-height: 1.6;
      margin-bottom: 14px;
      padding-left: 34px;
    }

    .layer-example {
      font-size: 12px;
      color: #86909c;
      line-height: 1.6;
      margin-top: 14px;
      padding-top: 12px;
      border-top: 1px dashed rgba(0, 0, 0, 0.06);
      padding-left: 34px;
    }

    .sub-condition {
      background: rgba(255, 255, 255, 0.65);
      border-radius: 8px;
      padding: 14px 16px;
      margin-top: 14px;
      border: 1px dashed rgba(245, 34, 45, 0.25);

      .sub-condition-title {
        font-size: 13px;
        font-weight: 600;
        color: #f5222d;
        margin-bottom: 12px;
      }

      .sub-condition-note {
        font-size: 12px;
        color: #606266;
        line-height: 1.6;
        margin-top: 10px;

        .hl-or {
          color: #f5222d;
          font-weight: 600;
          margin: 0 4px;
        }
      }
    }

    .manual-tag-tip {
      margin-top: 14px;
      padding: 10px 14px;
      background: rgba(255, 255, 255, 0.65);
      border-radius: 8px;
      font-size: 12px;
      color: #f5222d;
      line-height: 1.6;
      border-left: 3px solid #f5222d;

      i { margin-right: 6px; }
    }
  }

  .param-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px 32px;
    padding-left: 34px;
  }

  .param-item {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;

    .param-label {
      display: flex;
      align-items: center;
      font-size: 13px;
      color: #4e5969;
      line-height: 1.4;
      user-select: none;
      font-weight: 500;
    }

    .status-dot {
      display: inline-block;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      margin-right: 8px;
      flex-shrink: 0;
      cursor: pointer;
      transition: transform 0.15s;

      &:hover { transform: scale(1.4); }

      &.on {
        background: #52c41a;
        box-shadow: 0 0 0 2px rgba(82, 196, 26, 0.15);
      }
      &.off {
        background: #faad14;
        box-shadow: 0 0 0 2px rgba(250, 173, 20, 0.15);
      }
    }

    .param-input-wrap {
      display: flex;
      align-items: center;
      gap: 8px;

      .param-unit {
        font-size: 13px;
        color: #86909c;
        flex-shrink: 0;
      }
    }

    .hint-block {
      font-size: 11px;
      color: #86909c;
      line-height: 1.5;
      margin-top: 2px;
    }
  }
}

/* ==================== 统计分析参数 ==================== */
.stats-card {
  .stats-group {
    padding: 16px 0;
    border-bottom: 1px dashed #e5e6eb;

    &:first-child { padding-top: 4px; }
    &:last-child {
      border-bottom: none;
      padding-bottom: 0;
    }
  }

  .stats-group-title {
    display: flex;
    align-items: center;
    font-size: 13px;
    font-weight: 600;
    color: #1d2129;
    margin-bottom: 14px;

    .dot-blue {
      display: inline-block;
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: #1890ff;
      margin-right: 8px;
      flex-shrink: 0;
    }
  }

  .stats-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px 32px;
    padding-left: 14px;
  }

  .param-item {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;

    .param-label {
      display: flex;
      align-items: center;
      font-size: 13px;
      color: #4e5969;
      line-height: 1.4;
      user-select: none;
    }

    .status-dot {
      display: inline-block;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      margin-right: 8px;
      flex-shrink: 0;
      cursor: pointer;
      transition: transform 0.15s;

      &:hover { transform: scale(1.4); }

      &.on {
        background: #52c41a;
        box-shadow: 0 0 0 2px rgba(82, 196, 26, 0.15);
      }
      &.off {
        background: #faad14;
        box-shadow: 0 0 0 2px rgba(250, 173, 20, 0.15);
      }
    }

    .param-input-wrap {
      display: flex;
      align-items: center;
      gap: 8px;

      .param-unit {
        font-size: 13px;
        color: #86909c;
        flex-shrink: 0;
      }
    }

    .hint-block {
      font-size: 11px;
      color: #86909c;
      line-height: 1.5;
      margin-top: 2px;
    }
  }
}

/* ===== 底部按钮 ===== */
.params-card-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  padding: 20px 4px 24px;
  margin-top: 6px;

  .btn-outline {
    background: #fff;
    border: 1px solid #d9d9d9;
    border-radius: 8px;
    color: #4e5969;
    padding: 9px 22px;
    font-size: 13px;
    font-weight: 500;
    transition: all 0.2s;

    &:hover {
      background: #f5f7fa;
      border-color: #1890ff;
      color: #1890ff;
    }
  }

  .btn-primary {
    background: #1890ff;
    border: 1px solid #1890ff;
    color: #fff;
    border-radius: 8px;
    padding: 9px 22px;
    font-size: 13px;
    font-weight: 500;
    box-shadow: 0 2px 6px rgba(24, 144, 255, 0.25);
    transition: all 0.2s;

    &:hover:not(.is-disabled):not(:disabled) {
      background: #40a9ff;
      border-color: #40a9ff;
      box-shadow: 0 3px 10px rgba(24, 144, 255, 0.35);
    }

    &.is-disabled,
    &:disabled {
      background: #b6cad7 !important;
      border-color: #b6cad7 !important;
      color: rgba(255, 255, 255, 0.85) !important;
      box-shadow: none !important;
      cursor: not-allowed;
    }
  }
}

/* ==================== 输入数字框 ==================== */
/deep/ .el-input-number {
  width: 130px;

  .el-input__inner {
    text-align: left;
    padding-left: 12px;
    padding-right: 30px;
    font-size: 13px;
    color: #1d2129;
    border-radius: 8px;
    border-color: #d9d9d9;
    height: 32px;
    line-height: 32px;

    &:hover { border-color: #1890ff; }
    &:focus {
      border-color: #1890ff;
      box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.1);
    }
  }

  &.is-controls-right {
    .el-input-number__increase,
    .el-input-number__decrease {
      opacity: 0;
      transition: opacity 0.2s ease;
      pointer-events: none;
      position: absolute;
      right: 2px;
      width: 22px;

      i,
      .el-icon-arrow-up,
      .el-icon-arrow-down {
        display: none !important;
      }

      &::before {
        content: '';
        position: absolute;
        left: 50%;
        top: 50%;
        width: 0;
        height: 0;
        display: block;
        border-left: 4px solid transparent;
        border-right: 4px solid transparent;
      }
    }

    .el-input-number__increase {
      top: 2px;
      bottom: 50%;

      &::before {
        transform: translate(-50%, calc(-50% + 3px));
        border-bottom: 5px solid #909399;
      }
    }

    .el-input-number__decrease {
      top: 50%;
      bottom: 2px;

      &::before {
        transform: translate(-50%, calc(-50% - 3px));
        border-top: 5px solid #909399;
      }
    }

    &:hover,
    &:focus-within {
      .el-input-number__increase,
      .el-input-number__decrease {
        opacity: 1;
        pointer-events: auto;
      }
      .el-input-number__increase::before { border-bottom-color: #1890ff; }
      .el-input-number__decrease::before { border-top-color: #1890ff; }
    }
  }
}

/* ==================== 响应式 ==================== */
@media (max-width: 1200px) {
  .preview-card .preview-body {
    flex-direction: column;
    align-items: stretch;
  }
  .preview-card .donut-wrapper { margin: 0 auto; }
  .stats-card .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .patient-system-params { padding: 16px; }
  .preview-card .stat-cards { flex-wrap: wrap; }
  .preview-card .stat-card-item {
    flex: 1 1 calc(50% - 8px) !important;
  }
  .params-card .param-grid {
    grid-template-columns: 1fr;
    padding-left: 0;
  }
  .params-card .layer-desc,
  .params-card .layer-example { padding-left: 0; }
  .stats-card .stats-grid {
    grid-template-columns: 1fr;
    padding-left: 0;
  }
  .page-header {
    flex-direction: column;
    .header-right {
      margin-top: 14px;
      align-self: flex-start;
    }
  }
}
</style>

<!-- 非 scoped 样式 -->
<style lang="less">
.system-alert-box {
  border-radius: 12px !important;
  overflow: hidden;

  .el-message-box__header { padding: 16px 20px 8px; }
  .el-message-box__title {
    font-size: 15px !important;
    font-weight: 600 !important;
    color: #1d2129 !important;
  }
  .el-message-box__content {
    padding: 8px 20px 20px;
    color: #4e5969;
    font-size: 13px;
    line-height: 1.6;
  }
  .el-message-box__btns {
    display: flex !important;
    justify-content: center !important;
    padding: 8px 20px 18px !important;

    .el-button {
      float: none !important;
      min-width: 96px;
    }
  }
}

.patient-params-loading {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  z-index: 9999 !important;

  .el-loading-spinner {
    position: fixed !important;
    top: 50% !important;
    left: 50% !important;
    margin-top: -21px;
    transform: translate(-50%, -50%) !important;
  }

  .el-loading-text {
    font-size: 14px;
    color: #1890ff;
    margin-top: 10px;
  }

  .el-loading-spinner .circular .path { stroke: #1890ff; }
}
</style>